// Diário (0.65): dados privados e sensíveis, com a mesma proteção do cofre (AES-256-GCM no aparelho).
// Uma chave de dados aleatória cifra cada parte do diário (o dia, as metas, os hábitos, cada carta...).
// Essa chave fica guardada na conta só "embrulhada" pela senha do diário (PBKDF2-SHA256): o servidor só vê texto cifrado.
// Na forma "a mesma do app e do celular", depois da primeira abertura em cada aparelho a chave fica guardada no
// cofre do próprio celular (Keychain / Keystore) e abre com a biometria ou a senha do app.
import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import { getDoc, onDocChange, setDoc } from './store';
import type { AlertaItem, Anexo, BlocoPag, DiarioDoc, Item, ModoAviso, Quadro } from './types';
import { abrirSelado, deB64, deUtf8, derivar, getRandomBytes, paraB64, selar, utf8 } from '../lib/cofre';
import { today, uid } from '../lib/date';
import { N_ } from '../lib/i18n';
import { aoVoltar } from '../lib/ausencia';

const ITER = 60000;
const TESTE = 'myplanner-diario-ok';
const PREFIXO = 'd1:';

// ─── tipos do conteúdo (ficam cifrados) ───
export type Video = { id: string; titulo: string; url: string };
export type Resposta = { id: string; pergunta: string; texto: string };
export type DiaDiario = { humor?: number | null; clima?: { temp?: number | null; cond?: string; manual?: boolean } | null;
  quadro?: Quadro | null; fotos?: Anexo[]; videos?: Video[]; respostas?: Resposta[]; alt?: string };
export type TipoMeta = 'meta' | 'objetivo' | 'sonho';
export type Prazo = 'curto' | 'medio' | 'longo';
export type MetaVida = { id: string; titulo: string; icone: string; tipo: TipoMeta; prazo: Prazo; data?: string | null;
  passos: Item[]; blocos: BlocoPag[]; habitos: string[]; concluida?: string | null; criada: string; ordem?: number };
export type FreqHabito = { tipo: 'dia' } | { tipo: 'dias'; dias: number[] } | { tipo: 'vezes'; n: number } | { tipo: 'cada'; n: number; desde: string };
/** be: hábito que o Bem-Estar já registra (marcado sozinho, sem marcar duas vezes). */
export type LigacaoBE = 'agua' | 'treino' | 'cuidados' | 'refeicoes';
export type Habito = { id: string; nome: string; icone: string; freq: FreqHabito; meta?: string | null; be?: LigacaoBE | null;
  feitos: string[]; formado?: string | null; criado: string; ordem?: number };
export type FotoSonho = { id: string; arq: Anexo; legenda: string; meta?: string | null };
export type AvaliacaoRoda = { id: string; data: string; notas: Record<string, number> };
export type Roda = { areas: string[]; avaliacoes: AvaliacaoRoda[] };
export type Carta = { id: string; quadro?: Quadro | null; fotos?: Anexo[]; videos?: Video[] };
export type TipoInsp = 'texto' | 'frase' | 'video' | 'foto';
export type Inspiracao = { id: string; tipo: TipoInsp; texto: string; url?: string; arq?: Anexo; meta?: string | null; criado: string; ordem?: number };

// ─── chave aberta só na memória ───
let chave: Uint8Array | null = null;
const cache = new Map<string, any>();
const cifraVista = new Map<string, string>();   // texto cifrado que gerou o valor do cache (para notar mudanças de outro aparelho)
const pendentes = new Map<string, any>();
let timer: ReturnType<typeof setTimeout> | null = null;
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };

const doc = (): DiarioDoc => getDoc('diariop');
export const diarioConfigurado = () => !!(doc().chave || doc().rec);
export const diarioAberto = () => !!chave;
export const modoDiario = () => doc().modo ?? 'propria';
export function useDiario() {
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
  return { configurado: diarioConfigurado(), aberto: diarioAberto() };
}
// outro aparelho mudou o diário: a parte muda de novo quando for lida
onDocChange(id => { if (id === 'diariop' && chave) { const e = doc().e ?? {}; let mudou = false; for (const [k, c] of cifraVista) if (e[k] !== c && !pendentes.has(k)) { cache.delete(k); cifraVista.delete(k); mudou = true; } if (mudou) avisar(); } });

function cifrar(v: unknown) { if (!chave) throw new Error('diario-fechado'); return PREFIXO + selar(chave, utf8(JSON.stringify(v))); }
function decifrar<T>(c: string): T | undefined {
  if (!chave || !c?.startsWith(PREFIXO)) return undefined;
  try { return JSON.parse(deUtf8(abrirSelado(chave, c.slice(PREFIXO.length)))) as T; } catch { return undefined; }
}

/** Lê uma parte do diário (decifrada). Com o diário fechado devolve o padrão. */
export function ler<T>(k: string, padrao: T): T {
  if (!chave) return padrao;
  if (cache.has(k)) return cache.get(k);
  const c = doc().e?.[k];
  const v = c ? decifrar<T>(c) : undefined;
  if (c) cifraVista.set(k, c);
  const r = v === undefined ? padrao : v;
  cache.set(k, r);
  return r;
}
/** Grava uma parte (cifra e salva logo depois; várias mudanças seguidas viram um envio só). */
export function gravar<T>(k: string, v: T) {
  if (!chave) return;
  cache.set(k, v); pendentes.set(k, v); avisar();
  if (timer) clearTimeout(timer);
  timer = setTimeout(descarregar, 500);
}
export function apagarParte(k: string) {
  if (!chave) return;
  cache.delete(k); pendentes.delete(k);
  setDoc('diariop', d => { const e = { ...(d.e ?? {}) }; delete e[k]; return { ...d, e }; });
  avisar();
}
/** Envia agora o que está pendente (ao sair do diário, por exemplo). */
export function descarregar() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!chave || !pendentes.size) return;
  const novos: Record<string, string> = {};
  for (const [k, v] of pendentes) { novos[k] = cifrar(v); cifraVista.set(k, novos[k]); }
  pendentes.clear();
  setDoc('diariop', d => ({ ...d, e: { ...(d.e ?? {}), ...novos } }));
}

// ─── chave no cofre do aparelho (forma "a mesma do app e do celular") ───
// O nome fica fixo para o diário (did); diários de antes da 0.66 usam o sal da senha, como antes.
const nomeNoAparelho = () => 'diario_' + (doc().did ?? doc().chave?.sal ?? doc().rec?.sal ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 16);
async function guardarNoAparelho(k: Uint8Array) {
  if (Platform.OS === 'web') return;
  try { const SS = require('expo-secure-store'); await SS.setItemAsync(nomeNoAparelho(), paraB64(k)); } catch { /* sem o cofre do aparelho */ }
}
async function lerDoAparelho(): Promise<Uint8Array | null> {
  if (Platform.OS === 'web') return null;
  try { const SS = require('expo-secure-store'); const b = await SS.getItemAsync(nomeNoAparelho()); return b ? deB64(b) : null; } catch { return null; }
}
async function tirarDoAparelho() {
  if (Platform.OS === 'web') return;
  try { const SS = require('expo-secure-store'); await SS.deleteItemAsync(nomeNoAparelho()); } catch { /* ignora */ }
}
/** A chave deste aparelho ainda é a do diário? (confere com a parte de teste) */
function confere(k: Uint8Array) {
  const t0 = doc().e?._t;
  if (!t0) return true;
  try { return JSON.parse(deUtf8(abrirSelado(k, t0.slice(PREFIXO.length)))) === TESTE; } catch { return false; }
}

// ─── embrulhos da chave: pela senha do diário e pelo código de recuperação (0.66) ───
type Embrulho = { sal: string; iter: number; chave: string };
async function embrulhar(segredo: string, k: Uint8Array): Promise<Embrulho> {
  const sal = getRandomBytes(16);
  const ks = await derivar(segredo, sal, ITER);
  return { sal: paraB64(sal), iter: ITER, chave: selar(ks, new Uint8Array([...k, ...utf8(TESTE)])) };
}
async function desembrulhar(segredo: string, e: Embrulho | null | undefined): Promise<Uint8Array | null> {
  if (!e) return null;
  try {
    const ks = await derivar(segredo, deB64(e.sal), e.iter || ITER);
    const c = abrirSelado(ks, e.chave);
    return deUtf8(c.slice(32)) === TESTE ? c.slice(0, 32) : null;
  } catch { return null; }
}
export type TipoSenhaDiario = 'senha' | 'pin' | 'padrao';
/** A senha serve? senha: 6 ou mais; PIN: 4 a 6 números; padrão: 4 pontos ou mais. */
export function senhaValida(s: string, tipo: TipoSenhaDiario) {
  if (tipo === 'pin') return /^\d{4,6}$/.test(s);
  if (tipo === 'padrao') return s.split('-').length >= 4;
  return s.length >= 6;
}
const prefixoTipo = (tipo: TipoSenhaDiario) => (tipo === 'padrao' ? 'padrao:' : '');

// Código de recuperação: 6 grupos de 4 letras/números (sem I, O, 0 e 1, que confundem)
const ALFA = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function gerarCodigo() {
  const b = getRandomBytes(24);
  const l = Array.from(b, x => ALFA[x % ALFA.length]).join('');
  return l.match(/.{4}/g)!.join('-');
}
export const normalizarCodigo = (c: string) => c.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/O/g, '0').replace(/I/g, '1');
const codigoParaSegredo = (c: string) => 'rec:' + normalizarCodigo(c);

export const temSenhaDiario = () => !!doc().chave;
export const temCodigoRecuperacao = () => !!doc().rec;
export const tipoSenhaDiario = (): TipoSenhaDiario => doc().tipoSenha ?? 'senha';

/** Cria o diário. "app": sem senha do diário (chave no cofre do aparelho, liberada pelo desbloqueio do app);
 *  "propria": com a senha do diário. Os dois ganham um código de recuperação, que é devolvido para a pessoa guardar. */
export async function criarDiario(o: { modo: 'propria' | 'app'; senha?: string; tipo?: TipoSenhaDiario }): Promise<string> {
  const tipo = o.tipo ?? 'senha';
  if (o.modo === 'propria' && !senhaValida(o.senha ?? '', tipo)) throw new Error('senha-curta');
  const dados = getRandomBytes(32);
  const codigo = gerarCodigo();
  const rec = await embrulhar(codigoParaSegredo(codigo), dados);
  const senhaEmb = o.modo === 'propria' ? await embrulhar(prefixoTipo(tipo) + o.senha!, dados) : null;
  chave = dados;
  const teste = cifrar(TESTE);
  const did = paraB64(getRandomBytes(12));
  setDoc('diariop', d => ({ ...d, e: { ...(d.e ?? {}), _t: teste }, did, chave: senhaEmb, rec, modo: o.modo, tipoSenha: o.modo === 'propria' ? tipo : undefined }));
  if (o.modo === 'app') await guardarNoAparelho(dados);
  avisar();
  return codigo;
}
/** Abre com a senha do diário. Devolve false se a senha estiver errada. */
export async function abrirDiario(senha: string) {
  const k = await desembrulhar(prefixoTipo(tipoSenhaDiario()) + senha, doc().chave);
  if (!k) return false;
  chave = k;
  if (modoDiario() === 'app') await guardarNoAparelho(chave);
  avisar();
  return true;
}
/** Abre com o código de recuperação (esqueceu a senha ou aparelho novo). */
export async function abrirComCodigo(codigo: string) {
  const k = await desembrulhar(codigoParaSegredo(codigo), doc().rec);
  if (!k) return false;
  chave = k;
  if (modoDiario() === 'app') await guardarNoAparelho(chave);
  avisar();
  return true;
}
/** Abre com a chave guardada neste aparelho (depois da biometria ou da senha do app). */
export async function abrirPeloAparelho() {
  const k = await lerDoAparelho();
  if (!k || !confere(k)) return false;
  chave = k; avisar();
  return true;
}
export const temChaveNoAparelho = async () => !!(await lerDoAparelho());

/** Fecha o diário: a chave some da memória. */
export function fecharDiario() {
  descarregar();
  if (chave) chave.fill(0);
  chave = null; cache.clear(); cifraVista.clear();
  avisar();
}
/** Fixa o nome da chave no aparelho antes de trocar a senha (diários de antes da 0.66). */
function fixarDid() { const d = doc(); if (!d.did) { const n = d.chave?.sal ?? d.rec?.sal; if (n) setDoc('diariop', x => ({ ...x, did: n })); } }
/** Define uma senha nova do diário (o diário precisa estar aberto): depois do código de recuperação ou para trocar. */
export async function definirSenhaDiario(nova: string, tipo: TipoSenhaDiario) {
  if (!chave) return false;
  if (!senhaValida(nova, tipo)) throw new Error('senha-curta');
  fixarDid();
  const emb = await embrulhar(prefixoTipo(tipo) + nova, chave);
  setDoc('diariop', d => ({ ...d, chave: emb, tipoSenha: tipo }));
  if (modoDiario() === 'app') await guardarNoAparelho(chave);
  return true;
}
export async function trocarSenhaDiario(atual: string, nova: string, tipo: TipoSenhaDiario = tipoSenhaDiario()) {
  if (!senhaValida(nova, tipo)) throw new Error('senha-curta');
  if (!(await abrirDiario(atual)) || !chave) return false;
  return definirSenhaDiario(nova, tipo);
}
/** Gera um código de recuperação novo (o diário precisa estar aberto). O anterior deixa de valer. */
export async function novoCodigoRecuperacao(): Promise<string | null> {
  if (!chave) return null;
  fixarDid();
  const codigo = gerarCodigo();
  const rec = await embrulhar(codigoParaSegredo(codigo), chave);
  setDoc('diariop', d => ({ ...d, rec }));
  return codigo;
}
/** Troca a forma de abrir (o diário precisa estar aberto). Para "senha própria" sem senha ainda, passe a senha. */
export async function mudarModoDiario(modo: 'propria' | 'app', senha?: string, tipo?: TipoSenhaDiario) {
  if (modo === 'propria' && !temSenhaDiario()) {
    if (!senha || !tipo || !(await definirSenhaDiario(senha, tipo))) return false;
  }
  setDoc('diariop', d => ({ ...d, modo }));
  if (modo === 'app' && chave) await guardarNoAparelho(chave);
  if (modo === 'propria') await tirarDoAparelho();
  return true;
}
/** Apaga o conteúdo do diário (o Meu Dia, metas, hábitos, cartas e fotos). Os Projetos em Grupo continuam. */
export async function apagarDiario() {
  await tirarDoAparelho();
  fecharDiario();
  setDoc('diariop', d => ({ e: {}, ordem: d.ordem, arquivadas: d.arquivadas, conf: d.conf }));
}

// ─── lixeira (0.67): o que sai do Diário vai cifrado; só volta com o Diário aberto ───
/** Cifra uma parte do Diário para guardar na lixeira (o Diário precisa estar aberto). */
export const selarParaLixeira = (v: unknown) => cifrar(v);
/** Abre o que veio da lixeira. Itens antigos (sem cifra) passam direto. Diário fechado: erro (o item fica na lixeira). */
export function abrirDaLixeira<T>(c: unknown): T {
  if (typeof c !== 'string' || !c.startsWith(PREFIXO)) return c as T;
  if (!chave) throw new Error('diario-fechado');
  const v = decifrar<T>(c);
  if (v === undefined) throw new Error('diario-outra-chave');
  return v;
}

// ─── quando pedir a senha de novo (0.66, B1) ───
// "sair": toda vez que sair do Diário · "5min": depois de 5 minutos fora (padrão) · "app": só quando o app bloquear
export type RegraRepetir = 'sair' | '5min' | 'app';
export const regraRepetir = (): RegraRepetir => doc().conf?.repetir ?? '5min';
const CINCO_MIN = 5 * 60000;
let saiuDoDiarioEm = 0;   // quando a pessoa saiu da tela do Diário (0 = está nele)
/** A pessoa saiu da tela do Diário (outra aba, voltou para o início...). */
export function marcarSaidaDiario() {
  if (!chave) return;
  descarregar();
  if (regraRepetir() === 'sair') { fecharDiario(); return; }
  saiuDoDiarioEm = Date.now();
}
/** A pessoa voltou para a tela do Diário: fecha se passou do tempo. */
export function marcarEntradaDiario() {
  if (chave && saiuDoDiarioEm && regraRepetir() === '5min' && Date.now() - saiuDoDiarioEm >= CINCO_MIN) fecharDiario();
  saiuDoDiarioEm = 0;
}
/** O app voltou para a tela depois de ficar fora (fora = tempo fora de verdade; 0 = só perdeu o foco). */
export function aoVoltarAoApp(fora: number) {
  if (!chave || fora <= 0) return;
  const r = regraRepetir();
  if (r === 'sair' && !saiuDoDiarioEm) { fecharDiario(); return; }   // estava no Diário e saiu do app
  // texto 24 (1): 5 minutos fora do app fecha o Diário sempre (antes, se o app também bloqueava, o desbloqueio do app
  // abria o Diário junto, sem pedir)
  if (r === '5min' && fora >= CINCO_MIN) fecharDiario();
}
/** O app inteiro bloqueou: na regra "só quando o app bloquear", o Diário fecha junto. */
export function aoBloquearApp() { if (chave && regraRepetir() === 'app') fecharDiario(); }
aoVoltar(aoVoltarAoApp);

// ─── partes abertas (sem conteúdo) ───
export const mudarDiario = (fn: (d: DiarioDoc) => DiarioDoc) => setDoc('diariop', d => fn({ ...d, e: d.e ?? {} }));
export const confDiario = () => ({ lembrete: { on: false, hora: '21:00' }, seq: 'seguidos' as const, revisao: true, clima: true, repetir: '5min' as RegraRepetir, ...(doc().conf ?? {}) });
export const mudarConf = (p: Partial<NonNullable<DiarioDoc['conf']>>) => mudarDiario(d => ({ ...d, conf: { ...(d.conf ?? {}), ...p } }));

// ─── atalhos de leitura e escrita ───
export const chaveDia = (d: string) => `dia:${d}`;
export const lerDia = (d: string) => ler<DiaDiario>(chaveDia(d), {});
export function gravarDia(d: string, v: DiaDiario) {
  gravar(chaveDia(d), { ...v, alt: new Date().toISOString() });
  const idx = ler<{ dias: string[] }>('idx', { dias: [] });
  const tem = diaTemConteudo(v);
  if (tem && !idx.dias.includes(d)) gravar('idx', { dias: [...idx.dias, d].sort() });
  if (!tem && idx.dias.includes(d)) gravar('idx', { dias: idx.dias.filter(x => x !== d) });
}
export function diaTemConteudo(v: DiaDiario) {
  const q = v.quadro;
  return !!(v.humor || v.fotos?.length || v.videos?.length || v.respostas?.some(r => r.texto.trim())
    || (q && (q.tracos.length || q.textos.some(x => x.texto.trim()) || q.checks.length || q.tabelas?.length || q.imagens?.length)));
}
export const diasEscritos = () => ler<{ dias: string[] }>('idx', { dias: [] }).dias;

export const lerMetas = () => ler<MetaVida[]>('metas', []);
export const gravarMetas = (l: MetaVida[]) => gravar('metas', l);
export const lerHabitos = () => ler<Habito[]>('habitos', []);
export const gravarHabitos = (l: Habito[]) => gravar('habitos', l);
export const lerSonhos = () => ler<FotoSonho[]>('sonhos', []);
export const gravarSonhos = (l: FotoSonho[]) => gravar('sonhos', l);
export const AREAS_RODA = [N_('Saúde'), N_('Estudos'), N_('Finanças'), N_('Relacionamentos'), N_('Trabalho'), N_('Lazer')];
export const lerRoda = () => ler<Roda>('roda', { areas: AREAS_RODA, avaliacoes: [] });
export const gravarRoda = (r: Roda) => gravar('roda', r);
export const lerInsp = () => ler<Inspiracao[]>('insp', []);
export const gravarInsp = (l: Inspiracao[]) => gravar('insp', l);
export const lerFrase = () => ler<string | null>('frase', null);
export const lerMudar = () => ler<string>('mudar', '');

export const novaMeta = (p: Partial<MetaVida> = {}): MetaVida => ({ id: uid(), titulo: '', icone: '🎯', tipo: 'meta', prazo: 'curto', data: null,
  passos: [], blocos: [], habitos: [], concluida: null, criada: today(), ...p });
export const novoHabito = (p: Partial<Habito> = {}): Habito => ({ id: uid(), nome: '', icone: '✅', freq: { tipo: 'dia' }, meta: null, be: null, feitos: [], formado: null, criado: today(), ...p });

/** Lembrete diário como lista de alertas (texto 10, A4): o horário principal (com "Como avisar") e os extras. */
export function alertasDoLembrete(l: { on: boolean; hora: string; como?: ModoAviso; extras?: AlertaItem[] }): AlertaItem[] {
  return l.on ? [{ tipo: 'hora', hora: l.hora, como: l.como }, ...(l.extras ?? [])] : [];
}
/** De volta: o primeiro alerta é o horário principal; sem alertas, o lembrete desliga. */
export function lembreteDosAlertas(l: { on: boolean; hora: string }, itens: AlertaItem[]) {
  const [p, ...extras] = itens.filter(a => a.tipo === 'hora' && a.hora);
  return p ? { on: true, hora: p.hora!, como: p.como, extras } : { on: false, hora: l.hora };
}
